import { useCallback, useEffect, useRef, useState } from "react";
import { INIT_SIMPLIFIED_BANKROLL } from "../../constants";
import {
  INIT_COMPLETE_PREFERENCES,
  normalizeCompletePreferences,
} from "../../services/completePreferences";
import {
  normalizeRegistros,
  normalizeSimplifiedBankroll,
  normalizeTournaments,
} from "../../services/dataModel";
import {
  loadCommonTournaments,
  loadAppData,
  saveCompletePreferencesToSupabase,
  saveSimplifiedBankrollToSupabase,
  updateCachedAppData,
} from "../../services/supabaseData";

export default function useAppData(userId) {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dataStatus, setDataStatus] = useState(null);
  const [tournaments, setTournamentsRaw] = useState([]);
  const [commonTournaments, setCommonTournamentsRaw] = useState([]);
  const [registros, setRegistrosRaw] = useState([]);
  const [completePreferencesRaw, setCompletePreferencesRaw] = useState(
    normalizeCompletePreferences(INIT_COMPLETE_PREFERENCES)
  );
  const [simplifiedBankrollRaw, setSimplifiedBankrollRaw] = useState(
    normalizeSimplifiedBankroll(INIT_SIMPLIFIED_BANKROLL)
  );
  const completePreferencesSaveTimeoutRef = useRef(null);
  const simplifiedSaveTimeoutRef = useRef(null);
  const pendingCompletePreferencesRef = useRef(null);
  const pendingSimplifiedBankrollRef = useRef(null);

  const setTournaments = (val) => {
    setTournamentsRaw((prev) => {
      const next = normalizeTournaments(typeof val === "function" ? val(prev) : val);
      if (userId) {
        updateCachedAppData(userId, (current) => ({
          ...current,
          tournaments: next,
        }));
      }
      return next;
    });
  };

  const refreshCommonTournaments = useCallback(() => {
    if (!userId) return Promise.resolve([]);

    return loadCommonTournaments()
      .then((next) => {
        setCommonTournamentsRaw(next);
        updateCachedAppData(userId, (current) => ({
          ...current,
          commonTournaments: next,
        }));
        return next;
      })
      .catch((error) => {
        setDataStatus({
          type: "error",
          message: error.message || "Nao foi possivel carregar a base comum.",
        });
        return [];
      });
  }, [userId]);

  const setRegistros = (val) => {
    setRegistrosRaw((prev) => {
      const next = normalizeRegistros(typeof val === "function" ? val(prev) : val);
      if (userId) {
        updateCachedAppData(userId, (current) => ({
          ...current,
          registros: next,
        }));
      }
      return next;
    });
  };

  const persistCompletePreferences = useCallback(
    (nextPreferences, options = {}) => {
      if (!userId) return Promise.resolve();
      return saveCompletePreferencesToSupabase(userId, nextPreferences)
        .then(() => {
          updateCachedAppData(userId, (current) => ({
            ...current,
            completePreferences: nextPreferences,
          }));
        })
        .catch((error) => {
          if (!options.silent) {
            setDataStatus({
              type: "error",
              message: error.message || "Nao foi possivel salvar as preferencias.",
            });
          }
          throw error;
        });
    },
    [userId]
  );

  const queueCompletePreferencesSave = useCallback(
    (nextPreferences) => {
      if (!userId) return;
      pendingCompletePreferencesRef.current = nextPreferences;
      if (completePreferencesSaveTimeoutRef.current) {
        window.clearTimeout(completePreferencesSaveTimeoutRef.current);
      }

      completePreferencesSaveTimeoutRef.current = window.setTimeout(() => {
        const pendingPreferences = pendingCompletePreferencesRef.current;
        pendingCompletePreferencesRef.current = null;
        completePreferencesSaveTimeoutRef.current = null;
        persistCompletePreferences(pendingPreferences).catch(() => {});
      }, 400);
    },
    [persistCompletePreferences, userId]
  );

  const setCompletePreferences = (val) => {
    setCompletePreferencesRaw((prev) => {
      const next = normalizeCompletePreferences(
        typeof val === "function" ? val(prev) : val
      );
      if (userId) {
        updateCachedAppData(userId, (current) => ({
          ...current,
          completePreferences: next,
        }));
        queueCompletePreferencesSave(next);
      }
      return next;
    });
  };

  const persistSimplifiedBankroll = useCallback(
    (nextBankroll, options = {}) => {
      if (!userId) return Promise.resolve();
      return saveSimplifiedBankrollToSupabase(userId, nextBankroll)
        .then(() => {
          updateCachedAppData(userId, (current) => ({
            ...current,
            simplifiedBankroll: nextBankroll,
          }));
        })
        .catch((error) => {
          if (!options.silent) {
            setDataStatus({
              type: "error",
              message: error.message || "Nao foi possivel salvar no Supabase.",
            });
          }
          throw error;
        });
    },
    [userId]
  );

  const queueSimplifiedBankrollSave = useCallback(
    (nextBankroll) => {
      if (!userId) return;
      pendingSimplifiedBankrollRef.current = nextBankroll;
      if (simplifiedSaveTimeoutRef.current) {
        window.clearTimeout(simplifiedSaveTimeoutRef.current);
      }

      simplifiedSaveTimeoutRef.current = window.setTimeout(() => {
        const pendingBankroll = pendingSimplifiedBankrollRef.current;
        pendingSimplifiedBankrollRef.current = null;
        simplifiedSaveTimeoutRef.current = null;
        persistSimplifiedBankroll(pendingBankroll).catch(() => {});
      }, 700);
    },
    [persistSimplifiedBankroll, userId]
  );

  const setSimplifiedBankroll = (val) => {
    setSimplifiedBankrollRaw((prev) => {
      const next = normalizeSimplifiedBankroll(
        typeof val === "function" ? val(prev) : val
      );
      if (userId) {
        updateCachedAppData(userId, (current) => ({
          ...current,
          simplifiedBankroll: next,
        }));
        queueSimplifiedBankrollSave(next);
      }
      return next;
    });
  };

  const flushPendingSaves = useCallback(
    (options = {}) => {
      const saves = [];

      if (completePreferencesSaveTimeoutRef.current) {
        window.clearTimeout(completePreferencesSaveTimeoutRef.current);
        completePreferencesSaveTimeoutRef.current = null;
      }
      if (simplifiedSaveTimeoutRef.current) {
        window.clearTimeout(simplifiedSaveTimeoutRef.current);
        simplifiedSaveTimeoutRef.current = null;
      }

      if (pendingCompletePreferencesRef.current) {
        const pendingPreferences = pendingCompletePreferencesRef.current;
        pendingCompletePreferencesRef.current = null;
        saves.push(
          persistCompletePreferences(pendingPreferences, options).catch(() => {})
        );
      }

      if (pendingSimplifiedBankrollRef.current) {
        const pendingBankroll = pendingSimplifiedBankrollRef.current;
        pendingSimplifiedBankrollRef.current = null;
        saves.push(
          persistSimplifiedBankroll(pendingBankroll, options).catch(() => {})
        );
      }

      return Promise.allSettled(saves);
    },
    [persistCompletePreferences, persistSimplifiedBankroll]
  );

  useEffect(() => {
    return () => {
      flushPendingSaves({ silent: true });
    };
  }, [flushPendingSaves]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const flushSilently = () => {
      flushPendingSaves({ silent: true });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        flushSilently();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", flushSilently);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", flushSilently);
    };
  }, [flushPendingSaves]);

  useEffect(() => {
    if (!userId) {
      setDataLoaded(false);
      setTournamentsRaw([]);
      setCommonTournamentsRaw([]);
      setRegistrosRaw([]);
      setCompletePreferencesRaw(normalizeCompletePreferences(INIT_COMPLETE_PREFERENCES));
      setSimplifiedBankrollRaw(normalizeSimplifiedBankroll(INIT_SIMPLIFIED_BANKROLL));
      return;
    }

    let canceled = false;
    setDataLoaded(false);
    setDataStatus(null);

    loadAppData(userId)
      .then((data) => {
        if (canceled) return;
        setTournamentsRaw(data.tournaments);
        setCommonTournamentsRaw(data.commonTournaments);
        setRegistrosRaw(data.registros);
        setCompletePreferencesRaw(data.completePreferences);
        setSimplifiedBankrollRaw(data.simplifiedBankroll);
        setDataLoaded(true);
      })
      .catch((error) => {
        if (canceled) return;
        setDataStatus({
          type: "error",
          message: error.message || "Nao foi possivel carregar os dados do Supabase.",
        });
        setDataLoaded(true);
      });

    return () => {
      canceled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!dataStatus) return undefined;
    const timeout = window.setTimeout(() => setDataStatus(null), 3000);
    return () => window.clearTimeout(timeout);
  }, [dataStatus]);

  return {
    dataLoaded,
    dataStatus,
    setDataStatus,
    tournaments,
    commonTournaments,
    refreshCommonTournaments,
    setTournaments,
    registros,
    setRegistros,
    completePreferences: completePreferencesRaw,
    setCompletePreferences,
    simplifiedBankroll: simplifiedBankrollRaw,
    setSimplifiedBankroll,
  };
}
